import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import Orders from './components/Orders';
import Sales from './components/Sales';
import StockManagement from './components/StockManagement';
import Reports from './components/Reports';
import Suppliers from './components/Suppliers';
import Admin from './components/Admin';
import ChangePasswordModal from './components/ChangePasswordModal';
import './App.css';

// Composant pour protéger les routes
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

// Composant pour protéger les routes admin
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
}

function NavigationBar() {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Navigation groupée par catégories
  const navSections = [
    {
      title: 'Vue d\'ensemble',
      items: [
        { path: '/', label: 'Dashboard', icon: '📊', description: 'Vue générale' }
      ]
    },
    {
      title: 'Gestion',
      items: [
        { path: '/products', label: 'Produits', icon: '📦', description: 'Catalogue produits' },
        { path: '/stocks', label: 'Stocks', icon: '📋', description: 'Inventaire' },
        { path: '/suppliers', label: 'Fournisseurs', icon: '🏢', description: 'Contacts fournisseurs' }
      ]
    },
    {
      title: 'Transactions',
      items: [
        { path: '/orders', label: 'Commandes', icon: '🛒', description: 'Commandes fournisseurs' },
        { path: '/sales', label: 'Ventes', icon: '💰', description: 'Ventes enregistrées' }
      ]
    },
    {
      title: 'Analyse',
      items: [
        { path: '/reports', label: 'Rapports', icon: '📈', description: 'Statistiques' }
      ]
    }
  ];

  // Ajouter section admin si nécessaire
  if (user?.role === 'admin') {
    navSections.push({
      title: 'Système',
      items: [
        { path: '/admin', label: 'Administration', icon: '🔧', description: 'Paramètres système' }
      ]
    });
  }

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Obtenir les initiales de l'utilisateur
  const getUserInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      {/* Hamburger Button - Mobile Only */}
      <button 
        className={`hamburger-btn ${isMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-label="Menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Overlay - Mobile Only */}
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}

      {/* Navigation Menu */}
      <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
        {/* Enhanced Header */}
        <div className="nav-header">
          <div className="nav-user-profile">
            <div className="user-avatar">
              {user?.name ? getUserInitials(user.name) : '?'}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">
                {user?.role === 'admin' ? '👑 Administrateur' : 
                 user?.role === 'manager' ? '👔 Manager' : '💼 Caissier'}
              </div>
              {user?.shop_name && (
                <div className="user-shop">🏪 {user.shop_name}</div>
              )}
            </div>
          </div>
          <button className="close-menu-btn" onClick={closeMenu} aria-label="Fermer">✕</button>
        </div>

        {/* Navigation Sections */}
        <div className="nav-links-container">
          {navSections.map((section, idx) => (
            <div key={idx} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              <div className="nav-section-items">
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <div className="nav-link-content">
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-description">{item.description}</span>
                    </div>
                    {location.pathname === item.path && (
                      <span className="nav-active-indicator">●</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: User Info + Logout Button */}
        <div className="nav-desktop-user">
          <div className="desktop-user-info">
            <div className="desktop-user-avatar">
              {user?.name ? getUserInitials(user.name) : '?'}
            </div>
            <span className="desktop-user-name">{user?.name}</span>
          </div>
          <button onClick={logout} className="logout-btn-desktop" title="Déconnexion">
            <span className="logout-icon">🚪</span>
            <span className="logout-text">Déconnexion</span>
          </button>
        </div>

        {/* Mobile: Footer with logout */}
        <div className="nav-footer">
          <button onClick={() => { logout(); closeMenu(); }} className="logout-btn-mobile">
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Déconnexion</span>
          </button>
          <div className="nav-footer-info">
            <div className="app-version">v2.0.0</div>
          </div>
        </div>
      </nav>
    </>
  );
}

function AppContent() {
  const { isAuthenticated, login, user, updateUser } = useAuth();

  if (!isAuthenticated) {
    return <Login onLoginSuccess={login} />;
  }

  // Si l'utilisateur doit changer son mot de passe, afficher le modal
  if (user?.must_change_password) {
    return <ChangePasswordModal onPasswordChanged={updateUser} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>🏪 {user?.shop_name || 'Gestion de Boutique'}</h1>
        <NavigationBar />
      </header>
      
      <main className="App-main">
        <Routes>
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
          <Route path="/stocks" element={<PrivateRoute><StockManagement /></PrivateRoute>} />
          <Route path="/suppliers" element={<PrivateRoute><Suppliers /></PrivateRoute>} />
          <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
          <Route path="/sales" element={<PrivateRoute><Sales /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/login" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;